import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  fetchAdTelemetryErrorsAPI,
  fetchAdTelemetryEventsAPI,
  fetchAdTelemetryLoadPercentilesAPI,
  fetchAdTelemetryPlatformsAPI,
  fetchAdTelemetrySummaryAPI,
  fetchAdTelemetryTimeseriesAPI,
  fetchAdTelemetryWaitPercentilesAPI,
  fetchAdTelemetryWaitTimeseriesAPI,
} from "../../../../features/adTelemetry/adTelemetryAPI";
import styles from "./adAnalytics.module.css";

const RANGE_PRESETS = [
  { id: "1h", label: "آخر ساعة", ms: 60 * 60 * 1000 },
  { id: "24h", label: "آخر 24 ساعة", ms: 24 * 60 * 60 * 1000 },
  { id: "7d", label: "آخر 7 أيام", ms: 7 * 24 * 60 * 60 * 1000 },
  { id: "30d", label: "آخر 30 يوم", ms: 30 * 24 * 60 * 60 * 1000 },
];

const EVENT_FILTER_OPTIONS = [
  { value: "", label: "كل الأحداث" },
  { value: "ad_load_started", label: "ad_load_started" },
  { value: "ad_load_succeeded", label: "ad_load_succeeded" },
  { value: "ad_load_failed", label: "ad_load_failed" },
  { value: "ad_load_timeout", label: "ad_load_timeout" },
  { value: "ad_show_requested", label: "ad_show_requested" },
  { value: "ad_show_displayed", label: "ad_show_displayed" },
  { value: "ad_show_rewarded", label: "ad_show_rewarded" },
  { value: "ad_show_dismissed_early", label: "ad_show_dismissed_early" },
  { value: "ad_show_error", label: "ad_show_error" },
];

function formatMs(value) {
  if (value == null) return "—";
  if (value < 1000) return `${Math.round(value)}ms`;
  return `${(value / 1000).toFixed(value < 10_000 ? 2 : 1)}s`;
}

function formatPct(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(1)}%`;
}

function formatInt(value) {
  if (value == null) return "—";
  return Number(value).toLocaleString("en-US");
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", { hour12: false });
}

function formatChartTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function rateTone(rate, { goodAbove } = {}) {
  if (rate == null) return styles.tonalNeutral;
  if (goodAbove != null) {
    if (rate >= goodAbove) return styles.tonalGood;
    if (rate >= goodAbove - 10) return styles.tonalWarn;
    return styles.tonalBad;
  }
  return styles.tonalNeutral;
}

function latencyTone(ms, { goodBelow } = {}) {
  if (ms == null) return styles.tonalNeutral;
  if (goodBelow != null) {
    if (ms <= goodBelow) return styles.tonalGood;
    if (ms <= goodBelow * 2) return styles.tonalWarn;
    return styles.tonalBad;
  }
  return styles.tonalNeutral;
}

const KpiCard = ({ label, value, sub, tone }) => (
  <div className={`${styles.kpiCard} ${tone || ""}`}>
    <div className={styles.kpiLabel}>{label}</div>
    <div className={styles.kpiValue}>{value}</div>
    {sub ? <div className={styles.kpiSub}>{sub}</div> : null}
  </div>
);

const FunnelStep = ({ label, count, rate, of }) => (
  <div className={styles.funnelStep}>
    <div className={styles.funnelStepLabel}>{label}</div>
    <div className={styles.funnelStepValue}>{formatInt(count)}</div>
    {rate != null && of ? (
      <div className={styles.funnelStepRate}>
        {formatPct(rate)} <span className={styles.funnelStepOf}>من {of}</span>
      </div>
    ) : null}
  </div>
);

const AdAnalytics = () => {
  const [rangeId, setRangeId] = useState("24h");
  const [refreshTick, setRefreshTick] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [eventFilter, setEventFilter] = useState("");

  const [summary, setSummary] = useState(null);
  const [waitPct, setWaitPct] = useState(null);
  const [loadPct, setLoadPct] = useState(null);
  const [eventsSeries, setEventsSeries] = useState(null);
  const [waitSeries, setWaitSeries] = useState(null);
  const [errorBreakdown, setErrorBreakdown] = useState([]);
  const [platformBreakdown, setPlatformBreakdown] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const range = useMemo(() => {
    const preset = RANGE_PRESETS.find((p) => p.id === rangeId) || RANGE_PRESETS[1];
    const to = new Date();
    const from = new Date(to.getTime() - preset.ms);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [rangeId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        s,
        wp,
        lp,
        es,
        ws,
        eb,
        pb,
        re,
      ] = await Promise.all([
        fetchAdTelemetrySummaryAPI(range),
        fetchAdTelemetryWaitPercentilesAPI(range),
        fetchAdTelemetryLoadPercentilesAPI(range),
        fetchAdTelemetryTimeseriesAPI(range),
        fetchAdTelemetryWaitTimeseriesAPI(range),
        fetchAdTelemetryErrorsAPI(range),
        fetchAdTelemetryPlatformsAPI(range),
        fetchAdTelemetryEventsAPI(range, {
          limit: 200,
          event: eventFilter || undefined,
        }),
      ]);
      setSummary(s);
      setWaitPct(wp);
      setLoadPct(lp);
      setEventsSeries(es);
      setWaitSeries(ws);
      setErrorBreakdown(eb || []);
      setPlatformBreakdown(pb || []);
      setRecentEvents(re || []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [range, eventFilter]);

  useEffect(() => {
    load();
  }, [load, refreshTick]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => setRefreshTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const totals = summary?.totals || {};
  const rates = summary?.rates || {};

  const chartEvents = useMemo(() => {
    return (eventsSeries?.points || []).map((p) => ({
      ts: p.ts,
      tsLabel: formatChartTime(p.ts),
      requested: p.ad_show_requested || 0,
      displayed: p.ad_show_displayed || 0,
      rewarded: p.ad_show_rewarded || 0,
      dismissed: p.ad_show_dismissed_early || 0,
      showError: p.ad_show_error || 0,
      loadFailed: p.ad_load_failed || 0,
      loadTimeout: p.ad_load_timeout || 0,
    }));
  }, [eventsSeries]);

  const chartWait = useMemo(() => {
    return (waitSeries?.points || []).map((p) => ({
      ts: p.ts,
      tsLabel: formatChartTime(p.ts),
      avg: p.avg,
      p95: p.p95,
      n: p.n,
    }));
  }, [waitSeries]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLead}>
          <div className={styles.title}>تحليلات الإعلانات</div>
          <div className={styles.subtitle}>
            مراقبة دورة حياة الإعلانات: وقت التحميل، وقت الانتظار، معدل التعبئة،
            وأخطاء العرض.
          </div>
        </div>

        <div className={styles.controls}>
          <div className={styles.rangeButtons}>
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.id}
                className={`${styles.rangeButton} ${
                  rangeId === p.id ? styles.rangeButtonActive : ""
                }`}
                onClick={() => setRangeId(p.id)}
                type="button"
              >
                {p.label}
              </button>
            ))}
          </div>

          <label className={styles.autoRefreshToggle}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>تحديث تلقائي</span>
          </label>

          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => setRefreshTick((t) => t + 1)}
            disabled={loading}
          >
            {loading ? "جاري التحميل..." : "تحديث"}
          </button>
        </div>
      </header>

      {error ? (
        <div className={styles.errorBanner}>
          {error}
        </div>
      ) : null}

      <section className={styles.kpiGrid}>
        <KpiCard
          label="وقت الانتظار p50 (الضغط → ظهور الإعلان)"
          value={formatMs(waitPct?.p50)}
          sub={`من ${formatInt(waitPct?.n || 0)} عرض`}
          tone={latencyTone(waitPct?.p50, { goodBelow: 1500 })}
        />
        <KpiCard
          label="وقت الانتظار p95"
          value={formatMs(waitPct?.p95)}
          sub={`أقصى: ${formatMs(waitPct?.max)}`}
          tone={latencyTone(waitPct?.p95, { goodBelow: 4000 })}
        />
        <KpiCard
          label="وقت الانتظار p99"
          value={formatMs(waitPct?.p99)}
          sub="ذيل أبطأ المستخدمين"
          tone={latencyTone(waitPct?.p99, { goodBelow: 8000 })}
        />
        <KpiCard
          label="وقت التحميل p50"
          value={formatMs(loadPct?.p50)}
          sub={`من ${formatInt(loadPct?.n || 0)} تحميل ناجح`}
          tone={latencyTone(loadPct?.p50, { goodBelow: 2000 })}
        />
        <KpiCard
          label="وقت التحميل p95"
          value={formatMs(loadPct?.p95)}
          sub={`أقصى: ${formatMs(loadPct?.max)}`}
          tone={latencyTone(loadPct?.p95, { goodBelow: 6000 })}
        />
        <KpiCard
          label="معدل التعبئة (Fill rate)"
          value={formatPct(rates.fillRate)}
          sub={`${formatInt(totals.loadResolved)} طلب تحميل`}
          tone={rateTone(rates.fillRate, { goodAbove: 90 })}
        />
        <KpiCard
          label="معدل الانتهاء (Timeout)"
          value={formatPct(rates.loadTimeoutRate)}
          sub="تحميلات تعدت 12 ثانية"
          tone={rateTone(100 - (rates.loadTimeoutRate || 0), {
            goodAbove: 99,
          })}
        />
        <KpiCard
          label="معدل المكافأة"
          value={formatPct(rates.rewardRate)}
          sub={`${formatInt(totals.rewarded)} مكافأة من ${formatInt(
            totals.requested,
          )} ضغطة`}
          tone={rateTone(rates.rewardRate, { goodAbove: 80 })}
        />
      </section>

      <section className={styles.funnelSection}>
        <div className={styles.sectionTitle}>قمع العرض (Show Funnel)</div>
        <div className={styles.funnelRow}>
          <FunnelStep
            label="ضغطات Watch"
            count={totals.requested}
            of={null}
          />
          <div className={styles.funnelArrow}>←</div>
          <FunnelStep
            label="ظهر الإعلان"
            count={totals.displayed}
            rate={rates.displayedRate}
            of="الضغطات"
          />
          <div className={styles.funnelArrow}>←</div>
          <FunnelStep
            label="استلم المكافأة"
            count={totals.rewarded}
            rate={rates.rewardRate}
            of="الضغطات"
          />
          <div className={styles.funnelArrow}>←</div>
          <FunnelStep
            label="إغلاق مبكر"
            count={totals.dismissedEarly}
            rate={rates.dismissEarlyRate}
            of="الضغطات"
          />
          <div className={styles.funnelArrow}>←</div>
          <FunnelStep
            label="خطأ عرض"
            count={totals.showError}
            rate={rates.showErrorRate}
            of="الضغطات"
          />
        </div>
      </section>

      <section className={styles.chartsRow}>
        <div className={styles.chartCard}>
          <div className={styles.sectionTitle}>
            وقت الانتظار (متوسط + p95) بمرور الوقت
          </div>
          <div className={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={chartWait}
                margin={{ top: 10, right: 16, bottom: 0, left: 8 }}
              >
                <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="tsLabel"
                  stroke="var(--chart-axis)"
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  stroke="var(--chart-axis)"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => formatMs(v)}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--chart-tooltip-bg)",
                    border: "1px solid var(--chart-tooltip-border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value, name) => [
                    formatMs(value),
                    name === "avg" ? "متوسط" : "p95",
                  ]}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: "var(--chart-legend-text)" }}
                  formatter={(value) => (value === "avg" ? "متوسط" : "p95")}
                />
                <Line
                  type="monotone"
                  dataKey="avg"
                  stroke="var(--chart-1)"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="p95"
                  stroke="var(--chart-5)"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={styles.chartCard}>
          <div className={styles.sectionTitle}>الأحداث بمرور الوقت</div>
          <div className={styles.chartWrapper}>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart
                data={chartEvents}
                margin={{ top: 10, right: 16, bottom: 0, left: 8 }}
              >
                <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="tsLabel"
                  stroke="var(--chart-axis)"
                  tick={{ fontSize: 11 }}
                />
                <YAxis stroke="var(--chart-axis)" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: "var(--chart-tooltip-bg)",
                    border: "1px solid var(--chart-tooltip-border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: "var(--chart-legend-text)" }}
                />
                <Area
                  type="monotone"
                  dataKey="displayed"
                  stackId="1"
                  stroke="var(--chart-1)"
                  fill="var(--chart-1)"
                  fillOpacity={0.35}
                />
                <Area
                  type="monotone"
                  dataKey="rewarded"
                  stackId="1"
                  stroke="var(--chart-2)"
                  fill="var(--chart-2)"
                  fillOpacity={0.35}
                />
                <Area
                  type="monotone"
                  dataKey="dismissed"
                  stackId="1"
                  stroke="var(--chart-3)"
                  fill="var(--chart-3)"
                  fillOpacity={0.35}
                />
                <Area
                  type="monotone"
                  dataKey="showError"
                  stackId="1"
                  stroke="var(--chart-5)"
                  fill="var(--chart-5)"
                  fillOpacity={0.4}
                />
                <Area
                  type="monotone"
                  dataKey="loadFailed"
                  stackId="1"
                  stroke="var(--chart-4)"
                  fill="var(--chart-4)"
                  fillOpacity={0.35}
                />
                <Area
                  type="monotone"
                  dataKey="loadTimeout"
                  stackId="1"
                  stroke="var(--chart-9)"
                  fill="var(--chart-9)"
                  fillOpacity={0.35}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className={styles.tablesRow}>
        <div className={styles.tableCard}>
          <div className={styles.sectionTitle}>أكثر أكواد الأخطاء شيوعاً</div>
          {errorBreakdown.length === 0 ? (
            <div className={styles.emptyState}>لا توجد أخطاء في هذه الفترة 🎉</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>الحدث</th>
                  <th>كود الخطأ</th>
                  <th>العدد</th>
                </tr>
              </thead>
              <tbody>
                {errorBreakdown.map((row, idx) => (
                  <tr key={`${row.event}-${row.errorCode}-${idx}`}>
                    <td className={styles.mono}>{row.event}</td>
                    <td className={styles.mono}>{row.errorCode}</td>
                    <td className={styles.numCell}>{formatInt(row.n)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.tableCard}>
          <div className={styles.sectionTitle}>تقسيم المنصات (وقت الانتظار)</div>
          {platformBreakdown.length === 0 ? (
            <div className={styles.emptyState}>لا توجد بيانات</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>المنصة</th>
                  <th>العروض</th>
                  <th>المتوسط</th>
                  <th>الأقصى</th>
                </tr>
              </thead>
              <tbody>
                {platformBreakdown.map((row) => (
                  <tr key={row.platform}>
                    <td className={styles.mono}>{row.platform}</td>
                    <td className={styles.numCell}>{formatInt(row.n)}</td>
                    <td className={styles.numCell}>{formatMs(row.avg)}</td>
                    <td className={styles.numCell}>{formatMs(row.max)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className={styles.eventsCard}>
        <div className={styles.eventsHeader}>
          <div className={styles.sectionTitle}>أحدث الأحداث</div>
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className={styles.eventSelect}
          >
            {EVENT_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.eventsScroll}>
          {recentEvents.length === 0 ? (
            <div className={styles.emptyState}>لا توجد أحداث</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>التوقيت</th>
                  <th>الحدث</th>
                  <th>المدة</th>
                  <th>المنصة</th>
                  <th>كود الخطأ</th>
                  <th>المستخدم</th>
                  <th>showId / adId</th>
                </tr>
              </thead>
              <tbody>
                {recentEvents.map((row) => (
                  <tr key={row._id}>
                    <td className={styles.mono}>{formatDateTime(row.createdAt)}</td>
                    <td className={styles.mono}>{row.event}</td>
                    <td className={styles.numCell}>
                      {row.durationMs != null ? formatMs(row.durationMs) : "—"}
                    </td>
                    <td className={styles.mono}>{row.platform || "—"}</td>
                    <td className={styles.mono}>{row.errorCode || "—"}</td>
                    <td className={styles.mono}>{row.userId || "—"}</td>
                    <td className={`${styles.mono} ${styles.truncate}`}>
                      {row.showId || row.adId || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdAnalytics;
